import { fromEvent, merge } from 'rxjs';
import { switchMap, map, tap, catchError } from 'rxjs/operators';
import { showError } from './ui.js';

/**
 * Wires a play button and an audio element into a single state stream.
 * Clicking the button immediately swaps its icon to the loading spinner and
 * subscribes to load$; when load$ emits an audio src, setSrc is called and the
 * icon stays on the spinner. The audio element's media events then drive the
 * icon: loadstart and canplay keep the spinner, playing swaps to the stop icon,
 * and ended returns to the play triangle. A load failure — either from load$ or
 * from the audio error event — resets the icon to the play triangle and reports
 * the error via showError. Subscribe to keep the listeners alive; unsubscribe
 * to tear them down.
 *
 * setSrc defaults to assigning the src directly to the audio element.
 */
export function createAudioPlayback(
    button,
    audio,
    load$,
    setSrc = (src) => {
        audio.src = src;
    }
) {
    let click$ = fromEvent(button, 'click').pipe(
        tap(() => {
            button.textContent = '⏳';
        }),
        switchMap(() => load$),
        tap((src) => setSrc(src)),
        map(() => 'loading'),
        catchError((err) => {
            showError(err);
            return 'idle';
        })
    );

    let media$ = merge(
        fromEvent(audio, 'loadstart').pipe(map(() => 'loading')),
        fromEvent(audio, 'canplay').pipe(
            tap(() => audio.play().catch(() => {})),
            map(() => 'loading')
        ),
        fromEvent(audio, 'playing').pipe(map(() => 'playing')),
        fromEvent(audio, 'ended').pipe(map(() => 'idle')),
        fromEvent(audio, 'error').pipe(map(() => 'error'))
    );

    return merge(click$, media$).pipe(
        tap((state) => {
            if (state === 'loading') {
                button.textContent = '⏳';
            } else if (state === 'playing') {
                button.textContent = '⏹';
            } else {
                button.textContent = '▶';
                if (state === 'error') {
                    showError(new Error('Audio failed to load'));
                }
            }
        })
    );
}
